import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ClipboardList, Building2, Briefcase, HardHat, Hammer,
  DollarSign, BarChart3, ShieldCheck, Settings, LogOut, RadioTower, MessageSquare,
  Sparkles, Gauge, LineChart, TrendingUp, FileSpreadsheet, Target, CalendarDays,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { useT } from "@/i18n";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { key: "home", to: "/", module: "home", icon: LayoutDashboard },
  { key: "intake", to: "/intake", module: "intake", icon: Sparkles },
  { key: "work_orders", to: "/ordenes", module: "work_orders", icon: ClipboardList },
  { key: "schedule", to: "/programacion", module: "work_orders", icon: CalendarDays, internalOnly: true },
  { key: "tower", to: "/torre-de-control", module: "control_tower", icon: RadioTower },
  { key: "messages", to: "/mensajes", module: "messaging", icon: MessageSquare },
  { key: "properties", to: "/edificios", module: "properties", icon: Building2 },
  { key: "companies", to: "/companias", module: "companies", icon: Briefcase },
  { key: "subs", to: "/subcontratistas", module: "subs", icon: HardHat },
  { key: "projects", to: "/proyectos", module: "projects", icon: Hammer },
  { key: "estimates", to: "/estimados", module: "estimates", icon: Target },
  { key: "finance", to: "/finanzas", module: "finance", icon: DollarSign },
  { key: "dash_executive", to: "/tablero-ejecutivo", module: "dash_executive", icon: Gauge },
  { key: "dash_financial", to: "/tablero-financiero", module: "dash_financial", icon: LineChart },
  { key: "dash_operational", to: "/tablero-operativo", module: "dash_operational", icon: TrendingUp },
  { key: "dash_commercial", to: "/tablero-comercial", module: "dash_commercial", icon: BarChart3 },
  { key: "reports", to: "/reportes", module: "reports", icon: FileSpreadsheet },
  { key: "audit", to: "/auditoria", module: "audit", icon: ShieldCheck },
  { key: "settings", to: "/configuracion", module: "settings", icon: Settings },
];

const BRANDS = [
  { value: "all", key: "brandFilter.all" },
  { value: "bb", key: "brandFilter.bb" },
  { value: "betos", key: "brandFilter.betos" },
];

const INTERNAL_ROLES = ["owner", "socio_operativo", "operaciones", "ventas", "supervisor"];

export const Sidebar = () => {
  const { t } = useT();
  const { user, can, logout } = useAuth();
  const { brand, setBrand } = useBrand();
  const navigate = useNavigate();

  const items = NAV.filter((item) => can(item.module, "read") && (!item.internalOnly || INTERNAL_ROLES.includes(user?.rol)));

  return (
    <aside
      data-testid="sidebar"
      className="flex h-screen w-[220px] shrink-0 flex-col border-r border-line bg-paper"
    >
      <div className="px-4 pb-4 pt-5">
        <p className="text-[15px] font-semibold leading-tight text-ink">Beto's Handyman</p>
        <p className="mt-0.5 text-[11px] text-ink-tertiary">{t("app.tagline")}</p>
      </div>

      <div className="px-4 pb-3">
        <div className="flex divide-x divide-line border border-line" data-testid="brand-selector">
          {BRANDS.map((b) => (
            <button
              key={b.value}
              data-testid={`brand-option-${b.value}`}
              onClick={() => setBrand(b.value)}
              className={cn(
                "flex-1 py-1.5 text-[12px] font-medium transition-colors duration-[120ms]",
                brand === b.value ? "bg-ink text-paper" : "bg-paper text-ink-secondary hover:text-ink"
              )}
            >
              {b.value === "all" ? t("app.all") : t(b.key)}
            </button>
          ))}
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 pb-4">
        {items.map((item) => (
          <NavLink
            key={item.key}
            to={item.to}
            end={item.to === "/"}
            data-testid={`nav-${item.key}`}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2.5 border-l-2 px-2.5 py-2 text-[13px] transition-colors duration-[120ms]",
                isActive
                  ? "border-brand font-medium text-ink"
                  : "border-transparent text-ink-secondary hover:text-ink"
              )
            }
          >
            {({ isActive }) => (
              <>
                <item.icon className={cn("h-4 w-4 shrink-0", isActive ? "text-brand" : "text-ink-tertiary")} strokeWidth={1.5} />
                {t(`nav.${item.key}`)}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-line p-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu-trigger"
              className="flex w-full items-center gap-2.5 px-2 py-2 text-left transition-colors duration-[120ms] hover:bg-surface-sunken"
            >
              <div className="min-w-0 flex-1 leading-tight">
                <p className="truncate text-[12.5px] font-medium text-ink">{user?.nombre}</p>
                <p className="truncate font-mono text-[11px] uppercase tracking-[0.08em] text-ink-tertiary">
                  {t(`roles.${user?.rol}`)}
                </p>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-52 rounded-surface">
            <DropdownMenuItem
              data-testid="user-menu-profile"
              onClick={() => navigate("/configuracion?tab=profile")}
            >
              <Settings className="mr-2 h-3.5 w-3.5" />
              {t("app.profile")}
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="user-menu-logout" onClick={logout}>
              <LogOut className="mr-2 h-3.5 w-3.5" />
              {t("app.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
};
